import { ChangeEvent } from "react";

import { Button, Checkbox, LineClamp, useModal } from "@newsela/angelou";
import { withTrackEvent, objectTypes, actionNames } from "@newsela/snitch";

import { useSearchAdminPermissions } from "@/search/NewSearchPageMVP/provider/hooks";
import { useSearchPageContext } from "@/search/NewSearchPageMVP/provider/SearchPageProvider";
import { useStandardsSearchContext } from "@/search/NewSearchPageMVP/provider/StandardsSearchProvider";
import { FilterSection } from "@/search/NewSearchPageMVP/shared/FilterSection";
import { AgathaSchemaParamsEnum } from "@/search/NewSearchPageMVP/types/IParametersSchema";
import { StandardsModalProvider } from "@/search/NewSearchPageMVP/provider/StandardsModalProvider";
import { StandardsModal } from "./StandardsModal";
import {
  checkboxCss,
  listContainerCss,
  listItemCss,
  rootCss,
  standardDescriptionCss,
} from "./Standards.styles";

const CheckboxWithTrackEvent = withTrackEvent(Checkbox);
const ButtonWithTrackEvent = withTrackEvent(Button);

const standardsQueryParam = AgathaSchemaParamsEnum.related_to_standards;

export const Standards = () => {
  const {
    handlers: {
      getSearchParamValues,
      hasSearchParamValue,
      toggleSearchParamValue,
    },
  } = useSearchPageContext();

  const {
    top5Standards,
    updateSelectedStandardsOnChange,
    isLoadingStandardsResults,
  } = useStandardsSearchContext();

  const standardsInQuery = getSearchParamValues(standardsQueryParam);

  const [standardsModalProps, setStandardsModalActive] = useModal(false);
  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();

  if (!top5Standards) return null;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const standardParsed = JSON.parse(e.target.value);
    const standardContentId = standardParsed.content_id;

    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    updateSelectedStandardsOnChange(e.target.checked, standardParsed, true);
    toggleSearchParamValue(standardsQueryParam, standardContentId);
  };

  return (
    <FilterSection
      title="Standards"
      defaultCollapsed={!standardsInQuery.length}
    >
      <div css={rootCss}>
        <ul css={listContainerCss}>
          {top5Standards?.map((standard) => {
            const standardTitle =
              `${standard.display_fields.number_enhanced}` +
              (standard.total_content_tagged
                ? ` (${standard.total_content_tagged})`
                : "");

            return (
              <li key={standard.content_id} css={listItemCss}>
                <CheckboxWithTrackEvent
                  __cssFor={checkboxCss}
                  dataProps={{
                    "data-qa-selector": "filter-by-standard-checkbox",
                  }}
                  label={standardTitle}
                  value={JSON.stringify(standard)}
                  checked={hasSearchParamValue(
                    standardsQueryParam,
                    standard.content_id,
                  )}
                  onChange={handleChange}
                  objectType={objectTypes.content}
                  actionName={actionNames.filter}
                  streamProperties={{
                    description: "Select standard for search query",
                  }}
                />
                <LineClamp
                  lines={2}
                  text={standard.description || ""}
                  innerElement="p"
                  css={standardDescriptionCss}
                />
              </li>
            );
          })}
        </ul>
        <ButtonWithTrackEvent
          onClick={() => setStandardsModalActive(true)}
          dataProps={{
            "data-qa-selector": "open-view-all-standard-modal-button",
          }}
          disabled={isLoadingStandardsResults}
          flavor={Button.flavors.tertiary}
          objectType={objectTypes.content}
          actionName={actionNames.filter}
          streamProperties={{
            description: "View all standards modal for search querying",
          }}
        >
          View all standards
        </ButtonWithTrackEvent>
        <StandardsModalProvider>
          <StandardsModal
            modalProps={standardsModalProps}
            setStandardsModalActive={setStandardsModalActive}
          />
        </StandardsModalProvider>
      </div>
    </FilterSection>
  );
};
